import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

interface CalendarEventDetailRowProps {
  iconName?: IconName;
  iconContent?: ReactNode;
  content: ReactNode;
  className?: string;
  renderTooltip?: Renderer;
}

/*
 * Style.
 */

interface RowStyleProps {
  $hasTooltip: boolean;
}
const StyledRowDiv = styled.div<RowStyleProps>`
  display: grid;
  grid-column-gap: 12px;
  line-height: 20px;
  ${(p) => addTemplateColumns(p.$hasTooltip)}
`;

function addTemplateColumns(hasTooltip: boolean) {
  if (hasTooltip) {
    // Use 3 columns.
    return 'grid-template-columns: 16px 1fr auto;';
  }

  // Use 2 columns
  return 'grid-template-columns: 16px 1fr;';
}

const StyledIconWrapperDiv = styled.div`
  display: grid;
  justify-content: center;
  align-content: center;
  height: 16px;
  width: 16px;
`;

const StyledTooltipWrapperDiv = styled.div`
  display: grid;
  justify-content: center;
  align-content: start;

  /* Increase hover target without increasing the size of this div. */
  margin: 4px -3px 3px -3px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  padding: 3px;
`;

const StyledIcon = styled(Icon)`
  margin-top: 2px;
`;

/*
 * Component.
 */

export const CalendarEventDetailRow: FC<CalendarEventDetailRowProps> = (props) => (
  <StyledRowDiv className={props.className} $hasTooltip={Boolean(props.renderTooltip)}>
    <StyledIconWrapperDiv>{renderIcon(props)}</StyledIconWrapperDiv>
    {props.content}
    {maybeRenderTooltip(props)}
  </StyledRowDiv>
);

function renderIcon(props: CalendarEventDetailRowProps) {
  if (props.iconContent) {
    return props.iconContent;
  }

  const {iconName} = props;
  if (iconName) {
    return (
      <UserThemeConsumer>
        {(theme) => <StyledIcon name={iconName} color={theme.greys.shade70} />}
      </UserThemeConsumer>
    );
  }

  return null;
}

function maybeRenderTooltip(props: CalendarEventDetailRowProps) {
  if (!props.renderTooltip) {
    return null;
  }

  return (
    <StyledTooltipWrapperDiv>
      <StyledTooltipCoordinator render={props.renderTooltip} placement="top" maxWidth={240} delay={0} />
    </StyledTooltipWrapperDiv>
  );
}
